import React, { useState, useMemo } from 'react';
import { FiCalendar, FiPlus, FiX, FiChevronLeft, FiChevronRight, FiHome, FiTrash2 } from 'react-icons/fi';

/**
 * PREVIEW ONLY — Mobile-first redesign of the Admin Calendar PWA.
 * Route: /preview-calendar-ui (hidden, for client approval)
 * Uses mock data — no backend wiring.
 */

const VILLAS = [
  { id: '3bhk', short: '3BHK', name: 'Garden Escape', color: '#F57C20', bg: '#FFF3E8' },
  { id: '4bhk', short: '4BHK', name: 'Garden Retreat', color: '#7C3AED', bg: '#F3EEFF' },
  { id: '5bhk', short: '5BHK', name: 'Garden Delight', color: '#16A34A', bg: '#E7F8EE' },
  { id: '12bhk', short: '12BHK', name: 'Celebration Villa', color: '#DC2626', bg: '#FDECEC' },
];

// Mock booking/block data — what a typical manager screen looks like
const today = new Date();
const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
const fmt = (d) => d.toISOString().slice(0, 10);

const MOCK_BLOCKS = [
  { id: 'b1', villaId: '3bhk', start: fmt(addDays(today, 0)),  end: fmt(addDays(today, 2)),  source: 'website',  reason: 'Booked — Dhaval R. Shah',     guest: 'Dhaval Shah',   phone: '9820011223', amount: 25997 },
  { id: 'b2', villaId: '4bhk', start: fmt(addDays(today, 1)),  end: fmt(addDays(today, 4)),  source: 'airbnb',   reason: 'Airbnb — Confirmed',          guest: 'Airbnb Guest',  phone: null,         amount: null  },
  { id: 'b3', villaId: '5bhk', start: fmt(addDays(today, 3)),  end: fmt(addDays(today, 6)),  source: 'website',  reason: 'Booked — Priya Mehta',        guest: 'Priya Mehta',   phone: '9876543210', amount: 39997 },
  { id: 'b4', villaId: '12bhk', start: fmt(addDays(today, 7)), end: fmt(addDays(today, 10)), source: 'website', reason: 'Booked — Corporate Retreat',   guest: 'Vikram Patel',  phone: '9123456789', amount: 95997 },
  { id: 'b5', villaId: '3bhk', start: fmt(addDays(today, 12)), end: fmt(addDays(today, 14)), source: 'offline',  reason: 'Maintenance',                  guest: '—',             phone: null,         amount: null  },
];

const blocksForDate = (dateStr) =>
  MOCK_BLOCKS.filter(b => dateStr >= b.start && dateStr < b.end);

const SOURCE_LABEL = {
  website: { label: 'Website', bg: '#DCFCE7', fg: '#15803D' },
  airbnb:  { label: 'Airbnb',  bg: '#FEE2E2', fg: '#B91C1C' },
  offline: { label: 'Manual',  bg: '#E0E7FF', fg: '#4338CA' },
};

export default function PreviewCalendarUI() {
  const [selectedDate, setSelectedDate] = useState(today);
  const [stripOffset, setStripOffset] = useState(0); // weeks from now
  const [openCard, setOpenCard] = useState(null);

  // 14-day strip starting from today + offset weeks
  const stripDays = useMemo(() => {
    const start = addDays(today, stripOffset * 7);
    return Array.from({ length: 14 }, (_, i) => addDays(start, i));
  }, [stripOffset]);

  const todayStr = fmt(today);
  const selectedStr = fmt(selectedDate);

  // Agenda — show all blocks overlapping the next 21 days from selected date
  const agenda = useMemo(() => {
    const horizon = addDays(selectedDate, 21);
    return MOCK_BLOCKS
      .filter(b => b.end > selectedStr && b.start < fmt(horizon))
      .sort((a, b) => a.start.localeCompare(b.start));
  }, [selectedDate, selectedStr]);

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      {/* Top banner */}
      <div className="bg-[#F57C20] text-white text-center py-1.5 text-[10px] font-semibold tracking-wider uppercase">
        Preview · Mobile-First Calendar Redesign
      </div>

      {/* Sticky Header */}
      <div className="bg-[#1A1A2E] text-white px-4 pt-4 pb-3 sticky top-0 z-30 shadow-lg">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-xl font-bold tracking-tight">RGV Calendar</h1>
              <p className="text-white/40 text-[10px]">Welcome back, Vishwas</p>
            </div>
            <button className="px-3 py-1.5 rounded-full bg-white/10 text-[10px] font-bold uppercase tracking-wider">
              👁 Viewer
            </button>
          </div>

          {/* Villa color legend — bigger, clearer */}
          <div className="flex gap-1.5 overflow-x-auto pb-1 hide-scrollbar">
            {VILLAS.map(v => (
              <div key={v.id} className="flex items-center gap-1.5 bg-white/5 rounded-full px-3 py-1.5 flex-shrink-0">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: v.color }} />
                <span className="text-[11px] font-semibold">{v.short}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4 pb-32">
        {/* Quick stats row */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="bg-white rounded-2xl p-3 shadow-sm border border-gray-100">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">Today</p>
            <p className="text-2xl font-bold text-[#1A1A2E]">{blocksForDate(todayStr).length}</p>
            <p className="text-[10px] text-gray-500">villas occupied</p>
          </div>
          <div className="bg-white rounded-2xl p-3 shadow-sm border border-gray-100">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">Next 7d</p>
            <p className="text-2xl font-bold text-[#F57C20]">{agenda.slice(0, 7).length}</p>
            <p className="text-[10px] text-gray-500">upcoming</p>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-3 shadow-sm text-white">
            <p className="text-[10px] text-white/80 uppercase tracking-wider">Revenue</p>
            <p className="text-2xl font-bold">2.3L</p>
            <p className="text-[10px] text-white/80">this week</p>
          </div>
        </div>

        {/* Compact horizontal date strip */}
        <div className="bg-white rounded-2xl p-3 mb-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={() => setStripOffset(o => o - 1)}
              className="p-2 rounded-full hover:bg-gray-100"
            >
              <FiChevronLeft className="w-4 h-4" />
            </button>
            <p className="text-sm font-bold text-[#1A1A2E]">
              {stripDays[0].toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
            </p>
            <button
              onClick={() => setStripOffset(o => o + 1)}
              className="p-2 rounded-full hover:bg-gray-100"
            >
              <FiChevronRight className="w-4 h-4" />
            </button>
          </div>

          <div className="flex gap-1.5 overflow-x-auto pb-1 hide-scrollbar">
            {stripDays.map((d) => {
              const dStr = fmt(d);
              const blocks = blocksForDate(dStr);
              const isToday = dStr === todayStr;
              const isSelected = dStr === selectedStr;
              return (
                <button
                  key={dStr}
                  onClick={() => setSelectedDate(d)}
                  className={`flex-shrink-0 w-14 py-2 rounded-2xl flex flex-col items-center transition-all active:scale-95 ${
                    isSelected
                      ? 'bg-[#1A1A2E] text-white shadow-lg'
                      : isToday
                      ? 'bg-[#F57C20]/10 text-[#F57C20] border border-[#F57C20]/30'
                      : 'bg-gray-50 text-[#1A1A2E] hover:bg-gray-100'
                  }`}
                >
                  <span className={`text-[10px] uppercase font-bold ${isSelected ? 'text-white/60' : 'text-gray-500'}`}>
                    {d.toLocaleDateString('en-IN', { weekday: 'short' }).slice(0, 3)}
                  </span>
                  <span className="text-xl font-bold leading-tight">{d.getDate()}</span>
                  <div className="flex gap-0.5 mt-1 min-h-[6px]">
                    {blocks.slice(0, 4).map((b, i) => {
                      const v = VILLAS.find(x => x.id === b.villaId);
                      return <span key={i} className="w-1.5 h-1.5 rounded-full" style={{ background: v?.color }} />;
                    })}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected day breakdown */}
        <div className="bg-white rounded-2xl p-4 mb-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">Status for</p>
              <p className="text-lg font-bold text-[#1A1A2E]">
                {selectedDate.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' })}
              </p>
            </div>
            <span className={`text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-wider ${
              blocksForDate(selectedStr).length === VILLAS.length
                ? 'bg-red-100 text-red-700'
                : blocksForDate(selectedStr).length > 0
                ? 'bg-yellow-100 text-yellow-700'
                : 'bg-green-100 text-green-700'
            }`}>
              {blocksForDate(selectedStr).length === VILLAS.length ? 'Fully Booked' :
               blocksForDate(selectedStr).length > 0 ? `${VILLAS.length - blocksForDate(selectedStr).length} Available` :
               'All Free'}
            </span>
          </div>

          {/* Villa availability bars */}
          <div className="space-y-2">
            {VILLAS.map(v => {
              const block = blocksForDate(selectedStr).find(b => b.villaId === v.id);
              return (
                <div
                  key={v.id}
                  className={`flex items-center gap-3 p-3 rounded-xl ${block ? '' : 'border-2 border-dashed'}`}
                  style={{
                    background: block ? v.bg : '#FAFAFA',
                    borderColor: block ? 'transparent' : '#E5E7EB',
                  }}
                >
                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: v.color }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[#1A1A2E]">{v.short} <span className="text-gray-400 font-normal">· {v.name}</span></p>
                    {block ? (
                      <p className="text-[11px] text-gray-600 truncate">{block.reason}</p>
                    ) : (
                      <p className="text-[11px] text-green-600 font-semibold">Available · tap to block</p>
                    )}
                  </div>
                  {block && (
                    <span className="text-[9px] px-2 py-0.5 rounded font-bold uppercase tracking-wider flex-shrink-0"
                          style={{ background: SOURCE_LABEL[block.source].bg, color: SOURCE_LABEL[block.source].fg }}>
                      {SOURCE_LABEL[block.source].label}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Upcoming Agenda */}
        <div className="mb-4">
          <p className="text-[11px] text-gray-500 uppercase tracking-wider font-bold mb-2 px-1">Upcoming Stays · 3 weeks</p>
          <div className="space-y-2">
            {agenda.map(b => {
              const v = VILLAS.find(x => x.id === b.villaId);
              const isOpen = openCard === b.id;
              const startD = new Date(b.start);
              const endD = new Date(b.end);
              const nights = Math.round((endD - startD) / 86400000);
              return (
                <div
                  key={b.id}
                  onClick={() => setOpenCard(isOpen ? null : b.id)}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden active:scale-[0.98] transition-transform cursor-pointer"
                >
                  <div className="flex items-stretch">
                    <div className="w-1.5 flex-shrink-0" style={{ background: v.color }} />
                    <div className="flex-1 p-3 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-bold text-[#1A1A2E] truncate">{v.short} · {b.guest}</p>
                          <p className="text-[11px] text-gray-500 mt-0.5">
                            {startD.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                            {' → '}
                            {endD.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                            {' · '}
                            {nights} night{nights > 1 ? 's' : ''}
                          </p>
                        </div>
                        <span className="text-[9px] px-2 py-0.5 rounded font-bold uppercase tracking-wider flex-shrink-0"
                              style={{ background: SOURCE_LABEL[b.source].bg, color: SOURCE_LABEL[b.source].fg }}>
                          {SOURCE_LABEL[b.source].label}
                        </span>
                      </div>
                      {b.amount && (
                        <p className="text-[11px] text-[#F57C20] font-bold mt-1">Rs.{b.amount.toLocaleString()}</p>
                      )}
                    </div>
                  </div>
                  {isOpen && (
                    <div className="px-4 pb-3 pt-1 border-t border-gray-100 bg-gray-50 flex gap-2">
                      {b.phone && (
                        <a
                          href={`https://wa.me/91${b.phone}`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="flex-1 text-center py-2 px-3 bg-[#25D366] text-white rounded-lg text-xs font-bold"
                        >
                          WhatsApp Guest
                        </a>
                      )}
                      <button
                        onClick={(e) => e.stopPropagation()}
                        className="px-3 py-2 bg-white border border-red-200 text-red-600 rounded-lg text-xs font-semibold flex items-center gap-1"
                      >
                        <FiTrash2 className="w-3.5 h-3.5" /> Unblock
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Floating Block button */}
      <button
        className="fixed bottom-6 right-6 z-40 bg-[#F57C20] text-white rounded-full shadow-2xl px-5 py-3.5 flex items-center gap-2 font-bold text-sm active:scale-95 transition-transform"
        style={{ boxShadow: '0 12px 28px -6px rgba(245,124,32,0.55)' }}
      >
        <FiPlus className="w-5 h-5" /> Block Dates
      </button>

      <div className="bg-[#1A1A2E] text-center py-6 text-white/40 text-[10px]">
        Preview · Hidden Route<br/>Reply "ok" to ship this to your Calendar PWA
      </div>

      <style>{`
        .hide-scrollbar { scrollbar-width: none; }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
}
